/* eslint-disable eqeqeq */
/* eslint-disable camelcase */
import { Box, Button, IconButton, Typography } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import CountUp from 'react-countup'
import Icon from '@mdi/react'
import { mdiSaleOutline, mdiCloseCircleOutline } from '@mdi/js'

import colors from '../../../../assets/colors'
import { renderRouteRole } from '../../../../util/utils'
import { CustomSwitch } from '../../../../components/ui/custom-switch'
import { SNACKBAR } from '../../../main/MainActions'
import {
  cancelClientAddon,
  cancelClientAddonImmediate,
  contractClientAddon,
  fetchAddonCatalog,
  updateClientAddonDiscount,
  updateClientAddonQuantity,
} from '../../SubscriptionActions'
import StatusChip from '../Forms/StatusChip'
import ConfirmModal from '../Forms/ConfirmModal'
import QuantityDialog from '../Forms/QuantityDialog'
import {
  ADDON_STATUS_CANCEL_SCHEDULED,
  ADDON_STATUS_TONE,
  BOLETO_PRORATA_HINT_ADMIN,
  BOLETO_PRORATA_NOTICE,
  accessQuantity,
  formatAddonDate,
  hasScheduledReduction,
  isBoletoMethod,
  scheduledQuantity,
  scheduledReductionLabel,
} from '../Forms/addonHelpers'

import DiscountDialog from './DiscountDialog'

const useStyles = makeStyles(() => ({
  tile: {
    border: `1px solid ${colors.graylight}`,
    borderRadius: '10px',
    padding: '8px 12px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '8px',
  },
  tileInfo: {
    flex: 1,
    minWidth: 0,
  },
  metaRow: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '6px',
    marginTop: '2px',
  },
  caption: {
    fontSize: '11px',
    color: colors.textGray,
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  btnIncrement: {
    padding: '2px',
    color: colors.primary,
  },
  btnCancelImmediate: {
    padding: '2px',
    color: colors.error,
  },
  qtyButton: {
    minWidth: '58px',
    padding: '2px 8px',
    fontSize: '13px',
    fontWeight: 700,
    color: colors.primary,
    borderColor: colors.graylight,
    textTransform: 'none',
  },
}))

// a quantidade em vigor é um botão: alterar abre o modal de confirmação, que
// explica o efeito (cobrança na hora ao aumentar, acesso até o fim do ciclo ao
// reduzir) antes de disparar a chamada
const QuantityButton = ({ contract, onClick }) => {
  const classes = useStyles()
  const scheduled = scheduledQuantity(contract)

  return (
    <Button
      variant="outlined"
      size="small"
      className={classes.qtyButton}
      onClick={onClick}
      title="Alterar quantidade"
    >
      {scheduled != null
        ? `${accessQuantity(contract)} → ${scheduled}`
        : `× ${accessQuantity(contract)}`}
    </Button>
  )
}

// junta o catálogo (flat) com os add-ons em vigor do cliente (contracted),
// numa lista única por addon_service. contract != null => em vigor.
const buildAddonList = (catalog = [], contracted = []) => {
  const map = new Map()

  catalog.forEach(service => {
    map.set(service.id, { service, contract: null })
  })

  contracted.forEach(item => {
    const key = item.addon_service_id
    const service = map.get(key)?.service || item.addon_service || {}
    map.set(key, { service, contract: item })
  })

  // mostra serviços ativos ou qualquer add-on em vigor (mesmo de serviço inativo)
  return Array.from(map.values()).filter(
    entry => entry.contract || entry.service?.is_active
  )
}

const AddonTile = ({
  name,
  price,
  originalPrice,
  description,
  meta,
  footer,
  children,
}) => {
  const classes = useStyles()

  const contUpMoney = money => (
    <CountUp
      end={money}
      duration={0.7}
      decimal=","
      decimals={2}
      prefix="R$"
      separator="."
    />
  )

  // valores em centavos; preço efetivo menor que o de catálogo => valor cheio riscado
  const hasDiscount = originalPrice != null && originalPrice > price

  return (
    <Box className={classes.tile}>
      <Box className={classes.tileInfo}>
        <Typography style={{ fontWeight: 700, fontSize: '14px' }} noWrap>
          {name}
        </Typography>
        {description && (
          <Typography className={classes.caption} noWrap title={description}>
            {description}
          </Typography>
        )}
        <Box className={classes.metaRow}>
          {hasDiscount ? (
            <Box
              style={{ display: 'flex', alignItems: 'baseline', gap: '6px' }}
            >
              <Typography
                style={{
                  fontSize: '11px',
                  color: colors.textGray,
                  textDecoration: 'line-through',
                }}
              >
                {contUpMoney(originalPrice / 100)}
              </Typography>
              <Typography
                style={{
                  fontSize: '12px',
                  color: colors.textGray,
                  fontWeight: 700,
                }}
              >
                {contUpMoney(price / 100)}
              </Typography>
            </Box>
          ) : (
            <Typography style={{ fontSize: '12px', color: colors.textGray }}>
              {contUpMoney(price / 100)}
            </Typography>
          )}
          {meta}
        </Box>
        {footer}
      </Box>
      {children}
    </Box>
  )
}

const AddonsAdmin = ({ laundryClientId }) => {
  const classes = useStyles()
  const dispatch = useDispatch()

  const role = useSelector(state => state.auth.role)
  const canManage = renderRouteRole(['administrador'], role)

  const catalog = useSelector(state => state.subscription.adminCatalog)
  const addons = useSelector(state => state.subscription.adminAddons)
  const planDetails = useSelector(state => state.subscription.adminPlanDetails)

  // cliente em boleto: contratar/aumentar libera na hora e gera uma cobrança
  // avulsa do pró-rata, que precisa ser avisada a quem opera a tela
  const payInBoleto = isBoletoMethod(planDetails?.payment_method_id)

  const [dialog, setDialog] = useState(null)
  // add-on em edição de quantidade: { service, contract }
  const [quantityDialog, setQuantityDialog] = useState(null)
  // confirmação em aberto (cancelamentos), no padrão BaseFilterModal
  const [confirmRequest, setConfirmRequest] = useState(null)

  useEffect(() => {
    dispatch(fetchAddonCatalog())
  }, [])

  // sucessos de contratação/aumento não trazem data na message da API, então
  // aqui trocamos o toast pelo aviso da cobrança gerada (o snackbar é único)
  const notifyBoletoCharge = (ok, prefix) => {
    if (ok && payInBoleto) {
      dispatch({
        type: SNACKBAR.INFO,
        success: { message: `${prefix} ${BOLETO_PRORATA_NOTICE}` },
      })
    }
  }

  const handleContract = (addonServiceId, discount = 0, quantity = 1) => {
    dispatch(
      contractClientAddon(laundryClientId, addonServiceId, discount, quantity)
    ).then(ok => notifyBoletoCharge(ok, 'Serviço adicional liberado.'))
  }

  const handleCancel = (contractId, name) => {
    setConfirmRequest({
      title: 'Cancelar serviço adicional',
      description: `O serviço "${name}" continua liberado para o cliente até o fim do ciclo já pago e sai da próxima fatura. Não há devolução do valor já pago neste ciclo.`,
      confirmationText: 'Cancelar serviço',
      danger: true,
      onConfirm: () => dispatch(cancelClientAddon(contractId, laundryClientId)),
    })
  }

  const handleCancelImmediate = (contractId, name) => {
    setConfirmRequest({
      title: 'Cancelamento imediato',
      description: `Cancelar imediatamente o serviço "${name}"? O acesso é cortado agora e o valor já cobrado NÃO é estornado — nem a fatura do ciclo, nem o proporcional cobrado na contratação. Para devolver, é preciso estornar no painel do Pagar.me.`,
      confirmationText: 'Cancelar agora',
      danger: true,
      onConfirm: () =>
        dispatch(cancelClientAddonImmediate(contractId, laundryClientId)),
    })
  }

  const handleToggle = (entry, checked) => {
    if (checked) {
      setDialog({
        mode: 'contract',
        service: entry.service,
        initialDiscount: 0,
      })
      return
    }
    handleCancel(entry.contract.id, entry.service?.name)
  }

  // aumentar cobra o pró-rata na hora; reduzir vale sempre e só entra em vigor
  // no fim do ciclo pago (a message da API carrega a data e vai para o toast)
  const handleUpdateQuantity = (contract, quantity) => {
    const isIncrease = quantity > accessQuantity(contract)

    setQuantityDialog(null)
    dispatch(
      updateClientAddonQuantity(contract.id, quantity, laundryClientId)
    ).then(ok => {
      if (isIncrease) notifyBoletoCharge(ok, 'Quantidade liberada.')
    })
  }

  const handleEditDiscount = entry => {
    setDialog({
      mode: 'edit',
      service: entry.service,
      contract: entry.contract,
      initialDiscount: entry.contract.discount_percentage,
    })
  }

  const handleConfirmDiscount = (discount, quantity = 1) => {
    if (dialog?.mode === 'edit') {
      dispatch(
        updateClientAddonDiscount(dialog.contract.id, discount, laundryClientId)
      )
    } else if (dialog?.mode === 'contract') {
      handleContract(dialog.service.id, discount, quantity)
    }
    setDialog(null)
  }

  const renderDiscount = contract => {
    if (contract.discount_percentage >= 100) {
      return <StatusChip label="Grátis" tone="success" />
    }
    if (contract.discount_percentage > 0) {
      return (
        <StatusChip label={`−${contract.discount_percentage}%`} tone="info" />
      )
    }
    return null
  }

  const addonList = buildAddonList(catalog, addons)

  return (
    <Box>
      <Typography
        style={{
          fontWeight: 'bold',
          fontSize: '16px',
          color: colors.primary,
          margin: '4px 0 4px 0',
        }}
      >
        Serviços adicionais
      </Typography>

      {payInBoleto && (
        <Typography className={classes.caption} style={{ marginBottom: '8px' }}>
          {BOLETO_PRORATA_HINT_ADMIN}
        </Typography>
      )}

      {!addonList.length && (
        <Typography className={classes.caption}>
          Nenhum serviço adicional disponível.
        </Typography>
      )}

      {addonList.map(entry => {
        const { service, contract } = entry
        const isQuantity = service.type === 'quantity'
        const price = contract ? contract.price_snapshot : service.price
        // status 25 = cancelamento agendado: em vigor até o fim do ciclo,
        // switch desligado para permitir recontratar (reativar).
        const isCancelScheduled =
          contract && contract.status === ADDON_STATUS_CANCEL_SCHEDULED

        // redução agendada: o acesso segue na quantidade antiga (já paga) até
        // quantity_change_at, e só a próxima fatura cai para a nova
        const reductionFooter = hasScheduledReduction(contract) ? (
          <Typography className={classes.caption}>
            {scheduledReductionLabel(contract)} · a quantidade atual já está
            paga neste ciclo
          </Typography>
        ) : null

        const cancelFooter =
          contract && contract.effective_cancel_at ? (
            <Typography className={classes.caption}>
              Cancelamento agendado · em vigor até{' '}
              {formatAddonDate(contract.effective_cancel_at)} · sem devolução do
              valor já pago
            </Typography>
          ) : null

        return (
          <AddonTile
            key={`addon-${service.id}`}
            name={service.name}
            description={service.description}
            price={price}
            originalPrice={service.price}
            footer={
              <>
                {reductionFooter}
                {cancelFooter}
              </>
            }
            meta={
              <>
                {contract && (
                  <StatusChip
                    label={contract.status_text}
                    tone={ADDON_STATUS_TONE[contract.status] || 'neutral'}
                  />
                )}
                {contract && renderDiscount(contract)}
                {!contract && isQuantity && (
                  <StatusChip label="cobrado por unidade" tone="neutral" />
                )}
                {service.free_from_user_count && (
                  <StatusChip
                    label={`Grátis a partir de ${service.free_from_user_count} usuários`}
                    tone="success"
                  />
                )}
                {contract && contract.contracted_by === 'client' && (
                  <Typography className={classes.caption}>
                    contratado pelo cliente
                  </Typography>
                )}
              </>
            }
          >
            {canManage && (
              <Box className={classes.actions}>
                {contract && isQuantity && !isCancelScheduled && (
                  <QuantityButton
                    contract={contract}
                    onClick={() => setQuantityDialog({ service, contract })}
                  />
                )}
                {contract && (
                  <IconButton
                    className={classes.btnIncrement}
                    onClick={() => handleEditDiscount(entry)}
                    title="Editar desconto"
                    aria-label="editar desconto"
                  >
                    <Icon path={mdiSaleOutline} size={0.85} />
                  </IconButton>
                )}
                {contract && (
                  <IconButton
                    className={classes.btnCancelImmediate}
                    onClick={() =>
                      handleCancelImmediate(contract.id, service?.name)
                    }
                    title="Cancelar imediatamente"
                    aria-label="cancelar imediatamente"
                  >
                    <Icon path={mdiCloseCircleOutline} size={0.85} />
                  </IconButton>
                )}
                <CustomSwitch
                  checked={!!contract && !isCancelScheduled}
                  onChange={(e, checked) => handleToggle(entry, checked)}
                  inputProps={{ 'aria-label': `contratar ${service.name}` }}
                />
              </Box>
            )}
          </AddonTile>
        )
      })}

      <ConfirmModal
        confirm={confirmRequest}
        onClose={() => setConfirmRequest(null)}
      />

      <DiscountDialog
        dialog={dialog}
        onClose={() => setDialog(null)}
        onConfirm={handleConfirmDiscount}
      />

      <QuantityDialog
        dialog={quantityDialog}
        payInBoleto={payInBoleto}
        isAdmin
        onClose={() => setQuantityDialog(null)}
        onConfirm={qty => handleUpdateQuantity(quantityDialog.contract, qty)}
      />
    </Box>
  )
}

export default AddonsAdmin
